import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Divider,
  Group,
  Popover,
  ScrollArea,
  Skeleton,
  Stack,
  Text,
  Textarea,
  TextInput,
  Tooltip,
  UnstyledButton,
} from '@mantine/core';
import { IconCirclePlus, IconTemplate, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../lib/trpc';

type Channel = 'whatsapp' | 'instagram' | 'messenger';

const CHANNEL_LABEL: Record<Channel, string> = {
  whatsapp: 'WhatsApp',
  instagram: 'Instagram',
  messenger: 'Messenger',
};

/**
 * Saved quick-reply snippets, shared across the WhatsApp and Instagram inboxes.
 * Both channel groups are shown in either inbox (the picker just labels them);
 * picking inserts the snippet body into the composer. Inline-create defaults a
 * new snippet to the host inbox's `channel`. Backed by `chat.quickReplies.*`.
 */
export function QuickRepliesPopover({
  channel,
  onPick,
}: {
  channel: Channel;
  onPick: (body: string) => void;
}) {
  const [opened, setOpened] = useState(false);
  const utils = trpc.useUtils();
  const list = trpc.chat.quickReplies.list.useQuery(undefined, { enabled: opened });
  const create = trpc.chat.quickReplies.create.useMutation({
    onSuccess: () => {
      void utils.chat.quickReplies.list.invalidate();
      setNewTitle('');
      setNewBody('');
    },
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const remove = trpc.chat.quickReplies.delete.useMutation({
    onSuccess: () => void utils.chat.quickReplies.list.invalidate(),
    onError: (err) => notifications.show({ message: err.message, color: 'red' }),
  });
  const [newTitle, setNewTitle] = useState('');
  const [newBody, setNewBody] = useState('');

  const items = list.data ?? [];
  // Show the host channel's group first, then the others.
  const ALL: Channel[] = ['whatsapp', 'instagram', 'messenger'];
  const order: Channel[] = [channel, ...ALL.filter((c) => c !== channel)];
  const groups = order
    .map((ch) => ({ ch, rows: items.filter((qr) => (qr.channel ?? 'whatsapp') === ch) }))
    .filter((g) => g.rows.length > 0);

  return (
    <Tooltip label="Quick replies">
      <Box style={{ display: 'inline-flex' }}>
        <Popover
          opened={opened}
          onChange={setOpened}
          position="top-start"
          withArrow
          shadow="md"
          width={320}
        >
          <Popover.Target>
            <ActionIcon size="lg" variant="subtle" radius="xl" onClick={() => setOpened((o) => !o)}>
              <IconTemplate size={18} />
            </ActionIcon>
          </Popover.Target>
          <Popover.Dropdown p="xs">
            <Stack gap="xs">
              <Text size="sm" fw={600}>
                Quick replies
              </Text>
              {list.isPending ? (
                <Skeleton height={80} />
              ) : groups.length === 0 ? (
                <Text size="xs" c="dimmed">
                  No saved snippets yet — create one below.
                </Text>
              ) : (
                <ScrollArea.Autosize mah={240}>
                  <Stack gap={6}>
                    {groups.map((g) => (
                      <Stack key={g.ch} gap={4}>
                        <Text size="xs" c="dimmed" fw={600} tt="uppercase">
                          {CHANNEL_LABEL[g.ch]}
                        </Text>
                        {g.rows.map((qr) => (
                          <Group key={qr.id} wrap="nowrap" gap="xs">
                            <UnstyledButton
                              style={{ flex: 1, minWidth: 0 }}
                              onClick={() => {
                                onPick(qr.body ?? '');
                                setOpened(false);
                              }}
                            >
                              <Stack gap={0}>
                                <Text size="xs" fw={600} truncate>
                                  {qr.title}
                                  {qr.scope === 'personal' && (
                                    <Badge size="xs" variant="light" color="grape" ml={4}>
                                      personal
                                    </Badge>
                                  )}
                                </Text>
                                <Text size="xs" c="dimmed" lineClamp={2}>
                                  {qr.body ?? '—'}
                                </Text>
                              </Stack>
                            </UnstyledButton>
                            <ActionIcon
                              size="xs"
                              variant="subtle"
                              color="red"
                              onClick={() => remove.mutate({ id: qr.id })}
                            >
                              <IconTrash size={12} />
                            </ActionIcon>
                          </Group>
                        ))}
                      </Stack>
                    ))}
                  </Stack>
                </ScrollArea.Autosize>
              )}
              <Divider label={`New ${CHANNEL_LABEL[channel]} snippet`} labelPosition="left" />
              <TextInput
                size="xs"
                placeholder="Title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.currentTarget.value)}
              />
              <Textarea
                size="xs"
                placeholder="Body — what gets inserted into the composer"
                autosize
                minRows={2}
                maxRows={6}
                value={newBody}
                onChange={(e) => setNewBody(e.currentTarget.value)}
              />
              <Group gap="xs" justify="flex-end">
                <Button
                  size="xs"
                  variant="default"
                  leftSection={<IconCirclePlus size={12} />}
                  loading={create.isPending}
                  disabled={!newTitle.trim() || !newBody.trim()}
                  onClick={() =>
                    create.mutate({
                      scope: 'vendor',
                      channel,
                      title: newTitle.trim(),
                      body: newBody,
                      contentType: 'text',
                      isPinned: false,
                      sortOrder: 0,
                    })
                  }
                >
                  Save
                </Button>
              </Group>
            </Stack>
          </Popover.Dropdown>
        </Popover>
      </Box>
    </Tooltip>
  );
}
